import {
  DISPLAY_POSITION_LINE_COLUMN,
  DISPLAY_POSITION_XY,
  getOledI2cUi,
} from '@skyrover/blockly-blocks';
import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order } from './arduino-generator.js';
import { beginnerDisplayState, type OledConfig } from './beginner-display-state.js';

const OLED_NAME = 'skyroverOled';
const OLED_WIDTH = 128;
const OLED_HEIGHT = 64;

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function pinCode(pin: string): string {
  const trimmed = pin.trim();
  if (/^A\d+$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return trimmed;
}

function isEspBoard(): boolean {
  const id = arduinoGenerator.activeBoard?.id?.toLowerCase() ?? '';
  return id.includes('esp32') || id.includes('esp8266');
}

function defaultOledConfig(): OledConfig {
  const ui = getOledI2cUi();
  if (ui.mode === 'fixed') {
    return {
      address: '0x3C',
      scl: ui.scl,
      sda: ui.sda,
      selectablePins: false,
    };
  }
  return {
    address: '0x3C',
    scl: '22',
    sda: '21',
    selectablePins: true,
  };
}

function readOledConfigFromBlock(block: Block): OledConfig {
  const address = String(block.getFieldValue('ADDRESS') ?? '0x3C');
  const ui = getOledI2cUi();
  if (ui.mode === 'fixed') {
    return {
      address,
      scl: ui.scl,
      sda: ui.sda,
      selectablePins: false,
    };
  }
  return {
    address,
    scl: String(block.getFieldValue('SCL_PIN') ?? '22'),
    sda: String(block.getFieldValue('SDA_PIN') ?? '21'),
    selectablePins: true,
  };
}

function ensureOledSupport(config: OledConfig = beginnerDisplayState.oled ?? defaultOledConfig()): void {
  beginnerDisplayState.oled = config;
  arduinoGenerator.addInclude('#include <Wire.h>');
  arduinoGenerator.addInclude('#include <Adafruit_GFX.h>');
  arduinoGenerator.addInclude('#include <Adafruit_SSD1306.h>');

  if (!beginnerDisplayState.oledDeclared) {
    arduinoGenerator.addGlobal(
      `Adafruit_SSD1306 ${OLED_NAME}(${OLED_WIDTH}, ${OLED_HEIGHT}, &Wire, -1);`,
    );
    beginnerDisplayState.oledDeclared = true;
  }

  if (isEspBoard() || config.selectablePins) {
    arduinoGenerator.addSetupOnce(
      'beginner-oled-wire',
      `Wire.begin(${pinCode(config.sda)}, ${pinCode(config.scl)});`,
    );
  } else {
    arduinoGenerator.addSetupOnce('beginner-oled-wire', 'Wire.begin();');
  }

  arduinoGenerator.addSetupOnce(
    'beginner-oled-begin',
    `if (!${OLED_NAME}.begin(SSD1306_SWITCHCAPVCC, ${config.address})) {\n  for (;;);\n}`,
  );
  arduinoGenerator.addSetupOnce('beginner-oled-clear', `${OLED_NAME}.clearDisplay();`);
  arduinoGenerator.addSetupOnce('beginner-oled-show', `${OLED_NAME}.display();`);
}

function oledColor(color: string): string {
  return color === 'BLACK' ? 'SSD1306_BLACK' : 'SSD1306_WHITE';
}

function resolveOledCursor(block: Block): { x: number; y: number } {
  const mode = String(block.getFieldValue('POSITION_MODE') ?? DISPLAY_POSITION_LINE_COLUMN);
  const textSize = Math.max(1, beginnerDisplayState.oledTextSize);

  if (mode === DISPLAY_POSITION_XY) {
    return {
      x: Math.max(0, Math.round(Number(block.getFieldValue('X') ?? 0))),
      y: Math.max(0, Math.round(Number(block.getFieldValue('Y') ?? 0))),
    };
  }

  const line = Math.max(1, Math.round(Number(block.getFieldValue('LINE') ?? 1)));
  const column = Math.max(1, Math.round(Number(block.getFieldValue('COLUMN') ?? 1)));
  return {
    x: (column - 1) * 6 * textSize,
    y: (line - 1) * 8 * textSize,
  };
}

export function registerBeginnerOledGenerators(): void {
  arduinoGenerator.forBlock['beginner_oled_configure'] = (block: Block) => {
    ensureOledSupport(readOledConfigFromBlock(block));
    return statementPrefix(block, '// OLED configured\n');
  };

  arduinoGenerator.forBlock['beginner_oled_clear'] = (block: Block) => {
    ensureOledSupport();
    return statementPrefix(
      block,
      [`${OLED_NAME}.clearDisplay();`, `${OLED_NAME}.display();`, ''].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_oled_display_text'] = (block: Block) => {
    ensureOledSupport();
    const text = arduinoGenerator.valueToCode(block, 'TEXT', Order.NONE) || '""';
    const { x, y } = resolveOledCursor(block);
    const size = Math.max(1, beginnerDisplayState.oledTextSize);
    return statementPrefix(
      block,
      [
        `${OLED_NAME}.setTextSize(${size});`,
        `${OLED_NAME}.setTextColor(SSD1306_WHITE);`,
        `${OLED_NAME}.setCursor(${x}, ${y});`,
        `${OLED_NAME}.print(${text});`,
        `${OLED_NAME}.display();`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_oled_set_text_size'] = (block: Block) => {
    ensureOledSupport();
    const size = Math.min(5, Math.max(1, Math.round(Number(block.getFieldValue('SIZE') ?? 1))));
    beginnerDisplayState.oledTextSize = size;
    return statementPrefix(block, `${OLED_NAME}.setTextSize(${size});\n`);
  };

  arduinoGenerator.forBlock['beginner_oled_set_cursor'] = (block: Block) => {
    ensureOledSupport();
    const { x, y } = resolveOledCursor(block);
    return statementPrefix(block, `${OLED_NAME}.setCursor(${x}, ${y});\n`);
  };

  arduinoGenerator.forBlock['beginner_oled_draw_pixel'] = (block: Block) => {
    ensureOledSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    return statementPrefix(block, `${OLED_NAME}.drawPixel(${x}, ${y}, SSD1306_WHITE);\n`);
  };

  arduinoGenerator.forBlock['beginner_oled_draw_line'] = (block: Block) => {
    ensureOledSupport();
    const x1 = Math.round(Number(block.getFieldValue('X1') ?? 0));
    const y1 = Math.round(Number(block.getFieldValue('Y1') ?? 0));
    const x2 = Math.round(Number(block.getFieldValue('X2') ?? 0));
    const y2 = Math.round(Number(block.getFieldValue('Y2') ?? 0));
    return statementPrefix(
      block,
      `${OLED_NAME}.drawLine(${x1}, ${y1}, ${x2}, ${y2}, SSD1306_WHITE);\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_oled_draw_rectangle'] = (block: Block) => {
    ensureOledSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    const w = Math.round(Number(block.getFieldValue('WIDTH') ?? 0));
    const h = Math.round(Number(block.getFieldValue('HEIGHT') ?? 0));
    return statementPrefix(
      block,
      `${OLED_NAME}.drawRect(${x}, ${y}, ${w}, ${h}, SSD1306_WHITE);\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_oled_fill_rectangle'] = (block: Block) => {
    ensureOledSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    const w = Math.round(Number(block.getFieldValue('WIDTH') ?? 0));
    const h = Math.round(Number(block.getFieldValue('HEIGHT') ?? 0));
    return statementPrefix(
      block,
      `${OLED_NAME}.fillRect(${x}, ${y}, ${w}, ${h}, SSD1306_WHITE);\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_oled_draw_circle'] = (block: Block) => {
    ensureOledSupport();
    const cx = Math.round(Number(block.getFieldValue('CX') ?? 0));
    const cy = Math.round(Number(block.getFieldValue('CY') ?? 0));
    const r = Math.round(Number(block.getFieldValue('RADIUS') ?? 0));
    return statementPrefix(block, `${OLED_NAME}.drawCircle(${cx}, ${cy}, ${r}, SSD1306_WHITE);\n`);
  };

  arduinoGenerator.forBlock['beginner_oled_fill_circle'] = (block: Block) => {
    ensureOledSupport();
    const cx = Math.round(Number(block.getFieldValue('CX') ?? 0));
    const cy = Math.round(Number(block.getFieldValue('CY') ?? 0));
    const r = Math.round(Number(block.getFieldValue('RADIUS') ?? 0));
    return statementPrefix(block, `${OLED_NAME}.fillCircle(${cx}, ${cy}, ${r}, SSD1306_WHITE);\n`);
  };

  arduinoGenerator.forBlock['beginner_oled_draw_triangle'] = (block: Block) => {
    ensureOledSupport();
    const x1 = Math.round(Number(block.getFieldValue('X1') ?? 0));
    const y1 = Math.round(Number(block.getFieldValue('Y1') ?? 0));
    const x2 = Math.round(Number(block.getFieldValue('X2') ?? 0));
    const y2 = Math.round(Number(block.getFieldValue('Y2') ?? 0));
    const x3 = Math.round(Number(block.getFieldValue('X3') ?? 0));
    const y3 = Math.round(Number(block.getFieldValue('Y3') ?? 0));
    return statementPrefix(
      block,
      `${OLED_NAME}.drawTriangle(${x1}, ${y1}, ${x2}, ${y2}, ${x3}, ${y3}, SSD1306_WHITE);\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_oled_fill_screen'] = (block: Block) => {
    ensureOledSupport();
    const color = oledColor(String(block.getFieldValue('COLOR') ?? 'BLACK'));
    return statementPrefix(
      block,
      [`${OLED_NAME}.fillScreen(${color});`, `${OLED_NAME}.display();`, ''].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_oled_refresh'] = (block: Block) => {
    ensureOledSupport();
    return statementPrefix(block, `${OLED_NAME}.display();\n`);
  };
}

registerBeginnerOledGenerators();
